import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core'
import { RouterOutlet } from '@angular/router'
import { EventService } from './services/event.service'
import { Subscription } from 'rxjs'
import { LogService } from 'htv-lib'
import { CommonModule } from '@angular/common'
import { HTVButtonControllerDirective } from './app-button-controller.directive'
import { StateService } from './services/state.service'
import { LoaderComponent } from './pages/loader/loader.component'
import { LanguageService } from './services/language.service'
import { PlatformService } from './platforms/platform.service'
import { MonitoringService } from './services/monitoring.service'
import { PowerState } from './interfaces/power-state'
import { NetworkService } from './services/network.service'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { NetworkState } from './interfaces/network'
import { environment } from './environments/environment'
import { VolumeService } from './services/volume.service'
import { AlarmService } from './services/alarm.service'
import { RouterService } from './services/router.service'
import { LocationService } from './services/location.service'
import { VolumeComponent } from './layout/components/volume/volume.component'
import { ChannelWidgetComponent } from './layout/components/channel-widget/channel-widget.component'
import { WebSocketService } from './services/websocket.service'
import { StateDataService } from './services/state-data.service'
import { ExternalInputService } from './services/external-input.service'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    ChannelWidgetComponent,
    CommonModule,
    HTVButtonControllerDirective,
    LoaderComponent,
    MatSnackBarModule,
    RouterOutlet,
    VolumeComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [HTVButtonControllerDirective],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  eventSubscription?: Subscription

  powerState?: PowerState
  powerStateSubscription?: Subscription

  networkState?: NetworkState
  networkStateSubscription?: Subscription

  konamiMode = false
  konamiCode: string[] = []
  readonly _konamiCode = ['ARROW-DOWN', 'ARROW-UP', 'ARROW-DOWN', 'ARROW-RIGHT']
  readonly _konamiCodeStarter = ['c', 'COL-RED']

  constructor(
    private alarmService: AlarmService,
    private eventService: EventService,
    private externalInputService: ExternalInputService,
    private languageService: LanguageService,
    private locationService: LocationService,
    private logService: LogService,
    private monitoringService: MonitoringService,
    private networkService: NetworkService,
    private platformService: PlatformService,
    private routerService: RouterService,
    private stateService: StateService,
    private stateDataService: StateDataService,
    private volumeService: VolumeService,
    private webSocketService: WebSocketService
  ) {
    // do nothing
  }

  async ngOnInit(): Promise<void> {
    this.logService.init(environment.enableDebug)
    this.routerService.init()
    await this.platformService.init()
    this.stateDataService.init()
    this.webSocketService.init()
    this.languageService.init()
    this.volumeService.init()
    await this.networkService.init()
    await this.monitoringService.init()
    await this.externalInputService.init()
    this.locationService.init()
    await this.stateService.init()
    this.registerPowerStateListener()
    this.registerNetworkStateListener()
    this.alarmService.init()
  }

  ngAfterViewInit(): void {
    this.registerEventListener()
    this.registerKeyDownListener()
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe()
    this.powerStateSubscription?.unsubscribe()
    this.networkStateSubscription?.unsubscribe()
    this.alarmService.cleanup()
  }

  registerPowerStateListener(): void {
    this.powerStateSubscription?.unsubscribe()
    this.powerStateSubscription = this.monitoringService.powerState.subscribe((powerState: PowerState | undefined) => {
      if (this.powerState != powerState) {
        this.powerState = powerState
      }
    })
  }

  registerNetworkStateListener(): void {
    this.networkStateSubscription?.unsubscribe()
    this.networkStateSubscription = this.networkService.networkState$.subscribe(networkState => {
      if (!this.networkState) {
        this.networkState = networkState
      } else {
        this.networkState = networkState
      }
    })
  }

  registerEventListener(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe((key: string) => {
      switch (key) {
        default:
          break
      }
    })
  }

  registerKeyDownListener(): void {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (this.powerState == PowerState.STANDBY) return

      let key = event.key
      this.logService.log('Key: ' + key + ' ' + event.keyCode)
      if (event.keyCode >= 48 && event.keyCode <= 57) {
        key = '' + (event.keyCode - 48)
      } else if (event.keyCode >= 65 && event.keyCode <= 90) {
        key = event.key
      } else if (event.keyCode == 0) {
        switch (event.key) {
          case 'ColorF0Red': // PHILIPS RED BUTTON
            key = 'COL-RED'
            break
          case 'ColorF1Green': // PHILIPS GREEN BUTTON
            key = 'COL-GREEN'
            break
          case 'ColorF2Yellow': // PHILIPS YELLOW BUTTON
            key = 'COL-YELLOW'
            break
          case 'ColorF3Blue': // PHILIPS BLUE BUTTON
            key = 'COL-BLUE'
            break
          default:
            key = 'UNKNOWN : ' + event.key
            break
        }
      } else {
        switch (event.keyCode) {
          case 13:
            key = 'ENTER'
            event.preventDefault()
            break
          case 415:
            key = 'MEDIA-PLAY'
            break
          case 19:
            key = 'MEDIA-PAUSE'
            break
          case 413:
            key = 'MEDIA-STOP'
            break
          case 417:
            key = 'MEDIA-FASTFORWARD'
            break
          case 412:
            key = 'MEDIA-REWIND'
            break
          case 449:
          case 164: // PHILIPS MUTE
            key = 'VOL-MUTE'
            break
          case 448:
          case 25: // PHILIPS VOL DOWN
            key = 'VOL-DOWN'
            break
          case 447:
          case 24: // PHILIPS VOL UP
            key = 'VOL-UP'
            break
          case 428:
          case 34: // PHILIPS CHANNEL DOWN
            key = 'CHANNEL-DOWN'
            break
          case 427:
          case 33: // PHILIPS CHANNEL UP
            key = 'CHANNEL-UP'
            break
          case 700: // LG AD
            key = 'AD'
            break
          case 460: // LG SUBTITLE
          case 10221:
            key = 'SUBTITLE'
            break
          case 459: // LG TELETEXT
            key = 'TELETEXT'
            break
          case 406:
            key = 'COL-BLUE'
            break
          case 405:
            key = 'COL-YELLOW'
            break
          case 404:
            key = 'COL-GREEN'
            break
          case 403:
            key = 'COL-RED'
            break
          case 37:
            key = 'ARROW-LEFT'
            event.preventDefault()
            break
          case 38:
            key = 'ARROW-UP'
            event.preventDefault()
            break
          case 39:
            key = 'ARROW-RIGHT'
            event.preventDefault()
            break
          case 40:
            key = 'ARROW-DOWN'
            event.preventDefault()
            break
          case 461: // BACK LG
          case 10009:
            key = 'BACK'
            break
          case 1001: // EXIT LG
          case 10182: // EXIT button = Long press on RETURN button
            key = 'EXIT'
            break
          case 10071: // HOME
          case 10072: // SOURCE
          case 10133: // MENU
          case 10135: // TOOLS
          case 10497: // CONTENT
          case 602: // PORTAL
            key = 'HOME'
            break
          case 457: // INFO
          case 458: // GUIDE
          case 10073: // CHANNEL LIST
            key = 'INFO'
            break
          case 10234: // XF86Netflix
            key = 'NETFLIX'
            break
          case 65376: // Done for Tizen Keyboard IME
            key = 'DONE'
            break
          case 65385: // Cancel for Tizen Keyboard IME
            key = 'CANCEL'
            break
          default:
            key = 'UNKNOWN : ' + event.keyCode
            break
        }
      }
      if (this.konamiMode || this._konamiCodeStarter.includes(key)) {
        this.handleKonamiCode(key)
      }
      this.eventService.addEvent(key)
    })
  }

  handleKonamiCode(key: string): void {
    const resetKonamiCode = () => {
      this.konamiMode = false
      this.konamiCode = []
    }
    if (this.konamiMode) {
      if (this._konamiCode.includes(key)) {
        this.konamiCode.push(key)
        const index = this.konamiCode.length - 1
        if (this._konamiCode[index] === this.konamiCode[index]) {
          this.logService.debug(`[KonamiCode] ${this.konamiCode}`)
          if (this.konamiCode.length === this._konamiCode.length) {
            resetKonamiCode()
            this.routerService.goToRegisterPage()
          }
        } else {
          resetKonamiCode()
        }
      } else {
        resetKonamiCode()
      }
    } else {
      if (this._konamiCodeStarter.includes(key)) {
        this.logService.debug(`[KonamiCode] Start`)
        this.konamiMode = true
      } else {
        resetKonamiCode()
      }
    }
  }
}
